<script setup>
import {ref, watch, onUnmounted, onMounted, computed} from 'vue'
import { useGeoStore } from '@/stores/geo'
import {useRoute, useRouter} from 'vue-router'
import { API, HOST } from '@/utils/http'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import * as z from 'zod'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group"
import {Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import {CircleMinus, GripVertical, GitBranch, Plus, Type, Hash, ToggleLeft, AlignLeft, ListChecks, RefreshCw, Sparkles} from 'lucide-vue-next'
import ColorPicker from '@/components/ui/ColorPicker.vue'
import MediaUpload from '@/components/media/MediaUpload.vue'
import TemplateFieldsPreviewDialog from '@/components/templates/TemplateFieldsPreviewDialog.vue'
import GenerateTemplateDialog from '@/components/templates/GenerateTemplateDialog.vue'

const geoStore = useGeoStore()
const route = useRoute()
const router = useRouter()

const isAdminContext = computed(() => route.path.startsWith('/admin/'))
const projectId = computed(() => isAdminContext.value ? undefined : Number(route.params.project_id))
const orgId = computed(() => route.params.org_id)
const templateId = computed(() => route.params.template_id ? Number(route.params.template_id) : null)
const editMode = computed(() => !!templateId.value)
const aiGenerated = ref(false)
const editTemplate = ref(null)
const isLoading = ref(false)
const isSystemTemplate = ref(false)
const formKey = ref('create')
let draftSeedCounter = 0

const isProcessing = ref(false)
const selectedColor = ref('#E53E3E')
const mediaUploadRef = ref(null)

const geometryChoices = ['Point', 'LineString', 'Polygon']
const defaultTypes = [
  { name: 'Text', value: 0, icon: Type },
  { name: 'Multi-line text', value: 3, icon: AlignLeft },
  { name: 'Number', value: 1, icon: Hash },
  { name: 'Yes/No', value: 2, icon: ToggleLeft },
  { name: 'Choices', value: 4, icon: ListChecks },
]

const schema = computed(() => {
  const t = editTemplate.value
  return z.object({
    name: z.string().min(1, 'Name is required').default(t?.name || ''),
    description: z.string().default(t?.description || '').optional(),
    color: z.string().default(selectedColor.value),
    geometry: z.string().default(t?.geometry_type || geometryChoices[0]),
    shared: z.boolean().default(t?.shared || false),
    geofence: z.boolean().default(t?.geofence || false),
    geofenceEvents: z.string().nullable().default(null).optional(),
    imageRequired: z.boolean().default(t?.image_required || false),
    aiSummaryEnabled: z.boolean().default(t?.ai_summary_enabled || false),
    summaryPrompt: z.string().optional().default(t?.summary_prompt ?? '')
  })
})

const formSchema = computed(() => toTypedSchema(schema.value))

const isDataFields = ref(false)
const dataFields = ref([])
const draggedFieldIndex = ref(null)
const dragOverFieldIndex = ref(null)
const fieldRefs = ref([])

let tempIdCounter = 0
const generateTempId = () => `tmp_${++tempIdCounter}`

const toggleBranching = (field) => {
  field.branching_enabled = !field.branching_enabled
  if (!field.branching_enabled) {
    // Remove all children when disabling branching
    const descendantIds = getAllDescendantTempIds(field.temp_id)
    if (descendantIds.length > 0) {
      dataFields.value = dataFields.value.filter(f => !descendantIds.includes(f.temp_id))
    }
  }
}

const addDataField = () => {
  dataFields.value.push({ key: '', type: 0, options: [], temp_id: generateTempId(), parent_temp_id: null, trigger_operator: null, trigger_value: null, branching_enabled: false })
  setTimeout(() => {
    reinitializeDragDrop()
  }, 100)
}

const getChildInsertionIndex = (parentTempId) => {
  const fields = dataFields.value
  const parentIndex = fields.findIndex(f => f.temp_id === parentTempId)
  if (parentIndex === -1) {
    return fields.length
  }
  const descendantTempIds = new Set(getAllDescendantTempIds(parentTempId))
  let insertionIndex = parentIndex + 1
  while (
    insertionIndex < fields.length &&
    descendantTempIds.has(fields[insertionIndex].temp_id)
  ) {
    insertionIndex++
  }
  return insertionIndex
}

const addChildField = (parentTempId) => {
  const parent = dataFields.value.find(f => f.temp_id === parentTempId)
  const defaults = getDefaultTrigger(parent)
  const insertionIndex = getChildInsertionIndex(parentTempId)
  dataFields.value.splice(insertionIndex, 0, {
    key: '',
    type: 0,
    options: [],
    temp_id: generateTempId(),
    parent_temp_id: parentTempId,
    trigger_operator: defaults.operator,
    trigger_value: defaults.value,
    branching_enabled: false
  })
  setTimeout(() => {
    reinitializeDragDrop()
  }, 100)
}

const rootFields = computed(() => dataFields.value.filter(f => !f.parent_temp_id))

const getChildFields = (parentTempId) => {
  return dataFields.value.filter(f => f.parent_temp_id === parentTempId)
}

const hasBranching = (field) => !!field.branching_enabled

const triggerOperators = ['==', '!=', '>=', '<=', '>', '<', 'not_empty']

const getDefaultTrigger = (parentField) => {
  if (!parentField) return { operator: '==', value: '' }
  if (parentField.type === 2) return { operator: '==', value: 'true' }
  if (parentField.type === 4) return { operator: '==', value: parentField.options?.[0] || '' }
  if (parentField.type === 1) return { operator: '>=', value: '' }
  return { operator: 'not_empty', value: null }
}

const getAvailableOperators = (parentField) => {
  if (!parentField) return triggerOperators
  if (parentField.type === 2) return ['==']
  if (parentField.type === 4) return ['==']
  if (parentField.type === 1) return ['==', '!=', '>=', '<=', '>', '<']
  return ['==', '!=', 'not_empty']
}

const getValueOptions = (parentField) => {
  if (!parentField) return null
  if (parentField.type === 2) return [{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]
  if (parentField.type === 4) return (parentField.options || []).filter(o => o).map(o => ({ label: o, value: o }))
  return null
}

const getParentField = (child) => {
  return dataFields.value.find(f => f.temp_id === child.parent_temp_id)
}

const getTypeInfo = (typeValue) => defaultTypes.find(t => t.value === typeValue) || defaultTypes[0]

const handleTypeChange = (field, newType) => {
  if (field.type !== newType && field.branching_enabled) {
    const descendantIds = getAllDescendantTempIds(field.temp_id)
    if (descendantIds.length > 0) {
      dataFields.value = dataFields.value.filter(f => !descendantIds.includes(f.temp_id))
    }
    field.branching_enabled = false
  }
  field.type = newType
}

const getFieldIndex = (field) => dataFields.value.indexOf(field)

const getAllDescendantTempIds = (parentTempId) => {
  const children = dataFields.value.filter(f => f.parent_temp_id === parentTempId)
  const ids = children.map(c => c.temp_id)
  children.forEach(c => ids.push(...getAllDescendantTempIds(c.temp_id)))
  return ids
}

const reinitializeDragDrop = () => {
  cleanupFunctions.value.forEach(cleanup => cleanup && cleanup())
  cleanupFunctions.value = []

  fieldRefs.value.forEach((ref, index) => {
    if (ref && dataFields.value[index]) {
      const cleanup = setupFieldDragDrop(ref, dataFields.value[index].temp_id)
      if (cleanup) cleanupFunctions.value.push(cleanup)
    }
  })
}

const removeDataField = (index) => {
  const field = dataFields.value[index]
  if (field) {
    const descendantIds = getAllDescendantTempIds(field.temp_id)
    dataFields.value = dataFields.value.filter((f, i) => i !== index && !descendantIds.includes(f.temp_id))
  } else {
    dataFields.value.splice(index, 1)
  }
  updateFieldIndices()
}

const updateFieldIndices = () => {
  dataFields.value.forEach((field, index) => {
    field.index = index
  })
}

const onSubmit = async (values) => {
  isProcessing.value = true

  updateFieldIndices()

  const payload = {
    name: values.name,
    description: values.description || null,
    color: selectedColor.value,
    geometry_type: values.geometry,
    shared: values.shared,
    geofence: values.geofence,
    geofence_events: values.geofenceEvents,
    data_fields: dataFields.value.length > 0 ? dataFields.value.map((field, idx) => ({
      key: field.key,
      type: field.type,
      options: field.options,
      index: field.index !== undefined ? field.index : idx,
      temp_id: field.temp_id,
      parent_temp_id: field.parent_temp_id,
      trigger_operator: field.trigger_operator,
      trigger_value: field.trigger_value != null ? String(field.trigger_value) : null
    })) : null,
    image_required: values.imageRequired,
    ai_summary_enabled: values.aiSummaryEnabled,
    summary_prompt: values.aiSummaryEnabled ? (values.summaryPrompt ?? '') : null
  }

  if (isAdminContext.value) {
    payload.project_ids = []
    payload.is_system_template = isSystemTemplate.value
  } else {
    payload.project_id = projectId.value
  }

  const queuedFiles = mediaUploadRef.value?.getQueuedFiles() || []
  const iconFile = queuedFiles.length > 0 ? queuedFiles[0].file : undefined

  try {
    if (editMode.value) {
      await geoStore.updateReportTemplate({
        project_id: projectId.value,
        template: { ...editTemplate.value, ...payload },
        icon: iconFile
      })
    } else {
      await geoStore.createReportTemplate({
        template: payload,
        icon: iconFile
      })
    }

    if (!isAdminContext.value) {
      await geoStore.fetchProjectReportTemplates(projectId.value)
    }

    toast({
      title: `Template ${values.name} ${editMode.value ? 'updated' : 'created'}`,
    })

    if (isAdminContext.value) {
      router.push({ name: 'AdminTemplates' })
    } else {
      router.push({
        name: 'ProjectTemplates',
        params: { org_id: orgId.value, project_id: projectId.value }
      })
    }
  } catch (error) {
    toast({
      title: `Error ${editMode.value ? 'updating' : 'creating'} template`,
      description: error.message || 'An unexpected error occurred',
      variant: 'destructive'
    })
  } finally {
    isProcessing.value = false
  }
}

const loadTemplate = async () => {
  isLoading.value = true

  let template = null
  if (isAdminContext.value) {
    try {
      const response = await API({
        method: 'GET',
        url: `${HOST}/admin/templates/${templateId.value}`
      })
      template = response.data
    } catch {
      toast({ title: 'Template not found', variant: 'destructive' })
      router.push({ name: 'AdminTemplates' })
      isLoading.value = false
      return
    }
  } else {
    await geoStore.fetchProjectReportTemplates(projectId.value)
    const templates = geoStore.reportTemplates || []
    template = templates.find(t => t.id === templateId.value)
  }

  if (!template) {
    toast({ title: 'Template not found', variant: 'destructive' })
    if (isAdminContext.value) {
      router.push({ name: 'AdminTemplates' })
    } else {
      router.push({ name: 'ProjectTemplates', params: { org_id: orgId.value, project_id: projectId.value } })
    }
    isLoading.value = false
    return
  }

  editTemplate.value = template
  selectedColor.value = template.color
  isSystemTemplate.value = !!template.is_system_template

  if (template.data_fields && template.data_fields.length > 0) {
    isDataFields.value = true
    const idToTempId = {}
    dataFields.value = template.data_fields
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((field, index) => {
        const tempId = generateTempId()
        idToTempId[field.id] = tempId
        return {
          ...field,
          index: field.index ?? index,
          temp_id: tempId,
          trigger_operator: field.trigger_operator || null,
          trigger_value: field.trigger_value ?? null,
        }
      })

    const parentIds = new Set(template.data_fields.filter(f => f.parent_field_id).map(f => f.parent_field_id))
    dataFields.value.forEach(field => {
      field.parent_temp_id = field.parent_field_id ? (idToTempId[field.parent_field_id] || null) : null
      field.branching_enabled = parentIds.has(field.id)
    })
  }
  isLoading.value = false
}

const seedFromDraft = (draft) => {
  if (!draft) return
  aiGenerated.value = true
  editTemplate.value = {
    name: draft.name || '',
    description: draft.description || '',
    geometry_type: draft.geometry_type || geometryChoices[0],
    shared: draft.shared || false,
    geofence: draft.geofence || false,
    image_required: draft.image_required ?? true,
    ai_summary_enabled: draft.ai_summary_enabled || false,
    summary_prompt: draft.summary_prompt || '',
  }
  selectedColor.value = draft.color || selectedColor.value

  const fields = Array.isArray(draft.data_fields) ? draft.data_fields : []
  if (fields.length > 0) {
    isDataFields.value = true
    dataFields.value = fields
      .slice()
      .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
      .map((field, index) => ({
        key: field.key || '',
        type: field.type ?? 0,
        options: Array.isArray(field.options) ? field.options : [],
        index: field.index ?? index,
        temp_id: generateTempId(),
        parent_temp_id: null,
        trigger_operator: null,
        trigger_value: null,
        branching_enabled: false,
      }))
    setTimeout(() => reinitializeDragDrop(), 100)
  } else {
    isDataFields.value = false
    dataFields.value = []
  }

  formKey.value = `draft-${++draftSeedCounter}`
}

onMounted(() => {
  if (editMode.value) {
    loadTemplate()
  }
})

const findIndexByTempId = (tempId) => dataFields.value.findIndex(f => f.temp_id === tempId)

const setupFieldDragDrop = (element, tempId) => {
  if (!element) return

  const cleanup = combine(
    draggable({
      element,
      dragHandle: element.querySelector('.drag-handle'),
      getInitialData: () => ({ tempId }),
      onDragStart: () => {
        draggedFieldIndex.value = findIndexByTempId(tempId)
      },
      onDrop: () => {
        draggedFieldIndex.value = null
        dragOverFieldIndex.value = null
      },
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
          render: ({ container }) => {
            const currentIndex = findIndexByTempId(tempId)
            const preview = document.createElement('div')
            preview.className = 'bg-white shadow-lg rounded-md px-3 py-1.5 text-sm'
            preview.textContent = dataFields.value[currentIndex]?.key || 'Field ' + (currentIndex + 1)
            container.appendChild(preview)
          },
          nativeSetDragImage,
        })
      },
    }),

    dropTargetForElements({
      element,
      canDrop: ({ source }) => {
        return source.data.tempId !== tempId
      },
      getData: () => ({ tempId }),
      onDragEnter: () => {
        dragOverFieldIndex.value = findIndexByTempId(tempId)
      },
      onDragLeave: () => {
        dragOverFieldIndex.value = null
      },
      onDrop: ({ source }) => {
        const fromIndex = findIndexByTempId(source.data.tempId)
        const toIndex = findIndexByTempId(tempId)

        if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
          const [movedField] = dataFields.value.splice(fromIndex, 1)
          dataFields.value.splice(toIndex, 0, movedField)
          updateFieldIndices()
        }

        draggedFieldIndex.value = null
        dragOverFieldIndex.value = null
      },
    })
  )

  return cleanup
}

watch(dataFields, () => {
  updateFieldIndices()
}, { deep: true })

const cleanupFunctions = ref([])

watch(isDataFields, (newVal) => {
  if (newVal) {
    setTimeout(() => {
      reinitializeDragDrop()
    }, 100)
  }
})

onUnmounted(() => {
  cleanupFunctions.value.forEach(cleanup => cleanup && cleanup())
})

</script>

<template>
  <div class="p-3">
    <div class="mb-4">
      <div class="flex items-center justify-between">
        <h1 class="text-2xl font-bold">{{ editMode ? 'Edit template' : 'Create template' }}</h1>
        <GenerateTemplateDialog
          v-if="!editMode && !isAdminContext"
          :project-id="projectId"
          :trigger-label="aiGenerated ? 'Regenerate' : 'Generate'"
          :trigger-icon="aiGenerated ? RefreshCw : Sparkles"
          @generated="seedFromDraft"
        />
      </div>
    </div>

    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner class="w-6 h-6" />
    </div>

    <Form v-if="!isLoading" :key="editTemplate?.id || formKey" class="space-y-5" :validation-schema="formSchema" @submit="onSubmit" v-slot="{ getValues }" data-testid="template-form">
      <div class="flex space-x-4">
        <div class="flex-1">
          <FormField v-slot="{ componentField }" name="name">
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" data-testid="template-name-input" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
        <div class="w-20">
          <FormField v-slot="{ componentField }" name="color">
            <FormItem>
              <FormLabel>Color</FormLabel>
              <FormControl>
                <div class="pt-1">
                  <input type="hidden" v-bind="componentField" :value="selectedColor" />
                  <ColorPicker v-model="selectedColor" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>
      </div>

      <FormField v-slot="{ componentField }" name="description">
        <FormItem>
          <FormLabel>Description</FormLabel>
          <FormControl>
            <Textarea
              v-bind="componentField"
              placeholder="Optional description for this template"
              class="resize-none"
              rows="3"
              data-testid="template-description-input"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="geometry">
        <FormItem>
          <FormLabel>Geometry</FormLabel>
          <FormControl>
            <Select v-bind="componentField" data-testid="template-geometry-select">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem v-for="choice in geometryChoices" :key="choice" :value="choice">{{ choice }}</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ value, handleChange }" name="shared">
        <FormItem>
          <FormControl>
            <div class="flex items-center">
              <Switch
                :model-value="value"
                @update:model-value="handleChange"
                data-testid="template-shared-switch"
              />
              <Label class="ml-2">Public</Label>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ value, handleChange }" name="imageRequired">
        <FormItem>
          <FormControl>
            <div class="flex items-center">
              <Switch
                :model-value="value"
                @update:model-value="handleChange"
                data-testid="template-image-required-switch"
              />
              <Label class="ml-2">Image required</Label>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-if="getValues().geometry === 'Polygon'" v-slot="{ value, handleChange }" name="geofence">
        <FormItem>
          <FormControl>
            <div class="flex items-center">
              <Switch
                :model-value="value"
                @update:model-value="handleChange"
                data-testid="template-geofence-switch"
              />
              <Label class="ml-2">Geofence</Label>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <div>
        <FormField
          v-if="getValues().geofence"
          v-slot="{ componentField }"
          name="geofenceEvents"
        >
          <FormItem>
            <FormControl>
              <RadioGroup v-bind="componentField" class="flex space-x-3" data-testid="template-geofence-events">
                <FormItem class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem value="in" data-testid="geofence-event-in" />
                  </FormControl>
                  <FormLabel class="font-normal">In</FormLabel>
                </FormItem>
                <FormItem class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem value="out" data-testid="geofence-event-out" />
                  </FormControl>
                  <FormLabel class="font-normal">Out</FormLabel>
                </FormItem>
                <FormItem class="flex items-center space-y-0 gap-x-3">
                  <FormControl>
                    <RadioGroupItem value="both" data-testid="geofence-event-both" />
                  </FormControl>
                  <FormLabel class="font-normal">Both</FormLabel>
                </FormItem>
              </RadioGroup>
            </FormControl>
          </FormItem>
          <FormMessage />
        </FormField>
      </div>

      <div class="flex items-center">
        <Switch :model-value="isDataFields" @update:model-value="() => isDataFields = !isDataFields" data-testid="template-add-data-fields-switch" />
        <Label class="ml-2">Fields</Label>
      </div>

      <FormField v-slot="{ value, handleChange }" name="aiSummaryEnabled">
        <FormItem>
          <FormControl>
            <div class="flex items-center">
              <Switch
                :model-value="value"
                @update:model-value="handleChange"
                data-testid="template-ai-summary-switch"
              />
              <Label class="ml-2">AI summary</Label>
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-if="getValues().aiSummaryEnabled" v-slot="{ componentField }" name="summaryPrompt">
        <FormItem>
          <FormLabel>Summary Text</FormLabel>
          <FormControl>
            <Textarea
              v-bind="componentField"
              placeholder="What the AI summary should focus on"
              class="resize-none"
              rows="3"
              data-testid="template-summary-prompt-input"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-if="isDataFields" v-slot="{ value }" name="data_fields">
        <FormItem>
          <FormControl>
            <!-- Root fields with nested children containers -->
            <template v-for="field in rootFields" :key="field.temp_id">
              <!-- FIELD ROW (reused pattern for all levels) -->
              <div
                :ref="el => fieldRefs[getFieldIndex(field)] = el"
                class="flex flex-col space-y-2 mb-2 p-2 rounded border transition-all"
                :class="{
                  'opacity-50': draggedFieldIndex === getFieldIndex(field),
                  'border-blue-400 bg-blue-50': dragOverFieldIndex === getFieldIndex(field),
                  'border-gray-200': dragOverFieldIndex !== getFieldIndex(field)
                }"
              >
                <div class="flex flex-row items-center space-x-2">
                  <div class="drag-handle cursor-move flex items-center px-1 text-gray-400 hover:text-gray-600">
                    <GripVertical class="w-4 h-4" />
                  </div>
                  <div class="flex-1">
                    <Input v-model="field.key" placeholder="Label" :data-testid="`template-field-name-${getFieldIndex(field)}`" />
                  </div>
                  <div class="w-40">
                    <Select :model-value="field.type" @update:model-value="(val) => handleTypeChange(field, val)" :data-testid="`template-field-type-${getFieldIndex(field)}`">
                      <SelectTrigger><component :is="getTypeInfo(field.type).icon" class="w-4 h-4 shrink-0 text-muted-foreground" /><SelectValue /></SelectTrigger>
                      <SelectContent><SelectGroup>
                        <SelectItem v-for="t in defaultTypes" :key="t.value" :value="t.value"><div class="flex items-center gap-2"><component :is="t.icon" class="w-4 h-4 text-muted-foreground" />{{ t.name }}</div></SelectItem>
                      </SelectGroup></SelectContent>
                    </Select>
                  </div>
                  <Button v-if="field.type !== 3" type="button" variant="ghost" @click="toggleBranching(field)" class="px-1" :class="field.branching_enabled ? 'text-indigo-500' : 'text-gray-300 hover:text-indigo-400'" :title="field.branching_enabled ? 'Remove branching' : 'Add branching'">
                    <GitBranch class="w-4 h-4" />
                  </Button>
                  <Button type="button" variant="ghost" @click="removeDataField(getFieldIndex(field))" class="px-1 text-red-400 hover:text-red-500" :data-testid="`template-remove-field-${getFieldIndex(field)}`">
                    <CircleMinus class="w-5 h-5" />
                  </Button>
                </div>
                <div v-if="field.type === 4" class="border border-gray-300 rounded p-2 space-y-1">
                  <FormLabel>Options</FormLabel>
                  <div v-for="(option, optionIndex) in field.options" :key="optionIndex" class="flex flex-row space-x-2">
                    <div class="w-full"><Input v-model="field.options[optionIndex]" placeholder="Option" :data-testid="`template-field-option-${getFieldIndex(field)}-${optionIndex}`" /></div>
                    <div><Button type="button" variant="ghost" @click="field.options.splice(optionIndex, 1)" class="px-1 text-red-400 hover:text-red-500"><CircleMinus class="w-5 h-5" /></Button></div>
                  </div>
                  <Button type="button" @click="field.options.push('')" variant="secondary" size="sm">+ Add Option</Button>
                </div>
              </div>

              <!-- LEVEL 1 children container -->
              <div v-if="field.branching_enabled" class="ml-4 mb-3 pl-3 border-l-2 border-indigo-200">
                <template v-for="child in getChildFields(field.temp_id)" :key="child.temp_id">
                  <!-- Condition row -->
                  <div class="flex items-center gap-2 text-xs mb-1 mt-1">
                    <span class="text-gray-500 whitespace-nowrap">If entered value</span>
                    <Select :model-value="child.trigger_operator" @update:model-value="(op) => { child.trigger_operator = op; if (op === 'not_empty') child.trigger_value = null }">
                      <SelectTrigger class="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectGroup>
                        <SelectItem v-for="op in getAvailableOperators(field)" :key="op" :value="op">{{ op === 'not_empty' ? 'has value' : op }}</SelectItem>
                      </SelectGroup></SelectContent>
                    </Select>
                    <template v-if="child.trigger_operator !== 'not_empty'">
                      <template v-if="getValueOptions(field)">
                        <Select :model-value="child.trigger_value" @update:model-value="(val) => child.trigger_value = val">
                          <SelectTrigger class="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectGroup>
                            <SelectItem v-for="opt in getValueOptions(field)" :key="opt.value" :value="opt.value">{{ opt.label }}</SelectItem>
                          </SelectGroup></SelectContent>
                        </Select>
                      </template>
                      <template v-else>
                        <Input :model-value="child.trigger_value" @update:model-value="(val) => child.trigger_value = val" :type="field.type === 1 ? 'number' : 'text'" placeholder="Value" class="h-7 text-xs w-24" />
                      </template>
                    </template>
                  </div>
                  <!-- Child field -->
                  <div class="flex flex-col space-y-2 mb-2 p-2 rounded border border-gray-200">
                    <div class="flex flex-row items-center space-x-2">
                      <div class="flex-1"><Input v-model="child.key" placeholder="Label" /></div>
                      <div class="w-36">
                        <Select :model-value="child.type" @update:model-value="(val) => handleTypeChange(child, val)">
                          <SelectTrigger><component :is="getTypeInfo(child.type).icon" class="w-4 h-4 shrink-0 text-muted-foreground" /><SelectValue /></SelectTrigger>
                          <SelectContent><SelectGroup>
                            <SelectItem v-for="t in defaultTypes" :key="t.value" :value="t.value"><div class="flex items-center gap-2"><component :is="t.icon" class="w-4 h-4 text-muted-foreground" />{{ t.name }}</div></SelectItem>
                          </SelectGroup></SelectContent>
                        </Select>
                      </div>
                      <Button v-if="child.type !== 3" type="button" variant="ghost" @click="toggleBranching(child)" class="px-1" :class="child.branching_enabled ? 'text-indigo-500' : 'text-gray-300 hover:text-indigo-400'">
                        <GitBranch class="w-4 h-4" />
                      </Button>
                      <Button type="button" variant="ghost" @click="removeDataField(getFieldIndex(child))" class="px-1 text-red-400 hover:text-red-500">
                        <CircleMinus class="w-5 h-5" />
                      </Button>
                    </div>
                    <div v-if="child.type === 4" class="border border-gray-300 rounded p-2 space-y-1">
                      <FormLabel>Options</FormLabel>
                      <div v-for="(option, oi) in child.options" :key="oi" class="flex flex-row space-x-2">
                        <div class="w-full"><Input v-model="child.options[oi]" placeholder="Option" /></div>
                        <div><Button type="button" variant="ghost" @click="child.options.splice(oi, 1)" class="px-1 text-red-400 hover:text-red-500"><CircleMinus class="w-5 h-5" /></Button></div>
                      </div>
                      <Button type="button" @click="child.options.push('')" variant="secondary" size="sm">+ Add Option</Button>
                    </div>
                  </div>

                  <!-- LEVEL 2 children container -->
                  <div v-if="child.branching_enabled" class="ml-4 mb-3 pl-3 border-l-2 border-amber-200">
                    <template v-for="grandchild in getChildFields(child.temp_id)" :key="grandchild.temp_id">
                      <div class="flex items-center gap-2 text-xs mb-1 mt-1">
                        <span class="text-gray-500 whitespace-nowrap">If entered value</span>
                        <Select :model-value="grandchild.trigger_operator" @update:model-value="(op) => { grandchild.trigger_operator = op; if (op === 'not_empty') grandchild.trigger_value = null }">
                          <SelectTrigger class="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectGroup>
                            <SelectItem v-for="op in getAvailableOperators(child)" :key="op" :value="op">{{ op === 'not_empty' ? 'has value' : op }}</SelectItem>
                          </SelectGroup></SelectContent>
                        </Select>
                        <template v-if="grandchild.trigger_operator !== 'not_empty'">
                          <template v-if="getValueOptions(child)">
                            <Select :model-value="grandchild.trigger_value" @update:model-value="(val) => grandchild.trigger_value = val">
                              <SelectTrigger class="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectGroup>
                                <SelectItem v-for="opt in getValueOptions(child)" :key="opt.value" :value="opt.value">{{ opt.label }}</SelectItem>
                              </SelectGroup></SelectContent>
                            </Select>
                          </template>
                          <template v-else>
                            <Input :model-value="grandchild.trigger_value" @update:model-value="(val) => grandchild.trigger_value = val" :type="child.type === 1 ? 'number' : 'text'" placeholder="Value" class="h-7 text-xs w-24" />
                          </template>
                        </template>
                      </div>
                      <div class="flex flex-col space-y-2 mb-2 p-2 rounded border border-gray-200">
                        <div class="flex flex-row items-center space-x-2">
                          <div class="flex-1"><Input v-model="grandchild.key" placeholder="Label" /></div>
                          <div class="w-36">
                            <Select :model-value="grandchild.type" @update:model-value="(val) => handleTypeChange(grandchild, val)">
                              <SelectTrigger><component :is="getTypeInfo(grandchild.type).icon" class="w-4 h-4 shrink-0 text-muted-foreground" /><SelectValue /></SelectTrigger>
                              <SelectContent><SelectGroup>
                                <SelectItem v-for="t in defaultTypes" :key="t.value" :value="t.value"><div class="flex items-center gap-2"><component :is="t.icon" class="w-4 h-4 text-muted-foreground" />{{ t.name }}</div></SelectItem>
                              </SelectGroup></SelectContent>
                            </Select>
                          </div>
                          <Button type="button" variant="ghost" @click="removeDataField(getFieldIndex(grandchild))" class="px-1 text-red-400 hover:text-red-500">
                            <CircleMinus class="w-5 h-5" />
                          </Button>
                        </div>
                        <div v-if="grandchild.type === 4" class="border border-gray-300 rounded p-2 space-y-1">
                          <FormLabel>Options</FormLabel>
                          <div v-for="(option, oi) in grandchild.options" :key="oi" class="flex flex-row space-x-2">
                            <div class="w-full"><Input v-model="grandchild.options[oi]" placeholder="Option" /></div>
                            <div><Button type="button" variant="ghost" @click="grandchild.options.splice(oi, 1)" class="px-1 text-red-400 hover:text-red-500"><CircleMinus class="w-5 h-5" /></Button></div>
                          </div>
                          <Button type="button" @click="grandchild.options.push('')" variant="secondary" size="sm">+ Add Option</Button>
                        </div>
                      </div>
                    </template>
                    <Button type="button" @click="addChildField(child.temp_id)" variant="ghost" size="sm" class="text-xs text-indigo-600" :disabled="dataFields.length > 30">
                      <Plus class="w-3 h-3 mr-1" /> Add nested field
                    </Button>
                  </div>
                </template>
                <Button type="button" @click="addChildField(field.temp_id)" variant="ghost" size="sm" class="text-xs text-indigo-600" :disabled="dataFields.length > 30">
                  <Plus class="w-3 h-3 mr-1" /> Add nested field
                </Button>
              </div>
            </template>

            <div class="flex items-center gap-2">
              <Button type="button" @click="addDataField" variant="secondary" size="sm" :disabled="dataFields.length > 30" data-testid="template-add-field-button">+ New Field</Button>
              <TemplateFieldsPreviewDialog v-if="dataFields.length > 0" :fields="dataFields" />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <div>
        <Label class="pb-1">Icon</Label>
        <MediaUpload
          ref="mediaUploadRef"
          :allowed-types="['image']"
          :max-files="1"
          :max-file-size="5 * 1024 * 1024"
          :multiple="false"
          :defer-upload="true"
          compact
        />
      </div>

      <!-- Admin-Only Settings -->
      <div v-if="isAdminContext" class="px-3 py-3 bg-blue-50 border border-blue-200 rounded-md">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xs font-semibold text-blue-800 uppercase tracking-wide">Admin Only</span>
        </div>
        <div class="flex items-center">
          <Switch v-model="isSystemTemplate" />
          <Label class="ml-2 font-medium">System Template</Label>
        </div>
      </div>

      <div class="flex justify-end pt-4 border-t">
        <Button type="submit" :disabled="isProcessing" data-testid="template-create-button">
          <Spinner v-if="isProcessing" class="w-4 h-4 mr-2" />
          <span v-else>{{ editMode ? 'Save changes' : 'Create' }}</span>
        </Button>
      </div>
    </Form>
  </div>
</template>
